import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts'
import { getDisponibilidad } from '../../api/client'
import { currentLocale } from '../../i18n'
import {
  periodos, filtrarUnidades, matriz, porMes, disponibilidad, indicadoresPartes,
  formatoPct, banda, inicioMes, finMes, sumarMeses,
} from './disponibilidadLogica'

// Partes > Disponibilidad: the fleet's availability computed from the F/S
// partes (see disponibilidadLogica.js) -- KPI cards, a trend chart and the
// bus-by-day (or bus-by-month) grid of the old Disponibilidad spreadsheet.
// A red day opens the parte that took the bus out of service.

const BANDA_CELDA = {
  alta: 'bg-emerald-100 text-emerald-800',
  media: 'bg-lime-100 text-lime-800',
  baja: 'bg-amber-100 text-amber-800',
  critica: 'bg-rose-100 text-rose-700',
  nada: 'bg-gray-50 text-gray-300',
}
const BANDA_TEXTO = {
  alta: 'text-emerald-600', media: 'text-lime-600', baja: 'text-amber-600', critica: 'text-rose-600', nada: 'text-gray-300',
}
const COLOR_PROYECTO = ['#6366f1', '#f59e0b', '#14b8a6', '#ec4899']
const SIN_PROYECTO = '—'

const fmtMes = (iso, largo = false) => new Date(`${iso.slice(0, 7)}-15T12:00:00`)
  .toLocaleDateString(currentLocale(), largo ? { month: 'long', year: 'numeric' } : { month: 'short' })
const fmtDia = (iso) => new Date(`${iso}T12:00:00`)
  .toLocaleDateString(currentLocale(), { weekday: 'short', day: 'numeric', month: 'short' })
const inicialDia = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'narrow' })
const esFinde = (iso) => [0, 6].includes(new Date(`${iso}T12:00:00`).getDay())
const pct100 = (p) => (p == null ? null : Math.round(p * 1000) / 10)

// Rows grouped by project, in the order the projects appear.
function agrupar(filas) {
  const grupos = new Map()
  for (const f of filas) {
    const k = f.unidad.proyecto || SIN_PROYECTO
    if (!grupos.has(k)) grupos.set(k, [])
    grupos.get(k).push(f)
  }
  return [...grupos.entries()].map(([proyecto, filas]) => {
    const disp = filas.reduce((s, f) => s + f.disp, 0), fs = filas.reduce((s, f) => s + f.fs, 0)
    return { proyecto, filas, disp, fs, pct: disp + fs ? disp / (disp + fs) : null }
  })
}

function ordenarFilas(filas, orden) {
  if (orden !== 'peor') return filas
  return [...filas].sort((a, b) => (a.pct ?? 2) - (b.pct ?? 2) || b.fs - a.fs)
}

export default function Disponibilidad({ onOpenParte }) {
  const { t } = useTranslation('engineering')
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState('')
  const [proyecto, setProyecto] = useState('')
  const [buscar, setBuscar] = useState('')
  const [escala, setEscala] = useState('dia')      // 'dia' | 'mes'
  const [mes, setMes] = useState(null)             // 'YYYY-MM-01' shown in the day grid
  const [anio, setAnio] = useState(null)           // shown in the month grid
  const [orden, setOrden] = useState('codigo')     // 'codigo' | 'peor'

  useEffect(() => {
    let vivo = true
    getDisponibilidad()
      .then(d => { if (vivo) { setDatos(d); setError('') } })
      .catch(() => { if (vivo) setError(t('partes.disp.loadFailed')) })
    return () => { vivo = false }
  }, [t])

  const hoy = datos?.hoy
  const mesVisto = mes || (hoy && inicioMes(hoy))
  const anioVisto = anio || (hoy && +hoy.slice(0, 4))
  const proyectos = useMemo(
    () => (datos ? [...new Set(datos.unidades.map(u => u.proyecto).filter(Boolean))].sort() : []), [datos])
  const unidades = useMemo(
    () => (datos ? filtrarUnidades(datos.unidades, { proyecto, buscar }) : []), [datos, proyecto, buscar])

  if (error) return <p className="text-red-500 text-sm">{error}</p>
  if (!datos) return <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>

  const irAMes = (iso) => { setMes(inicioMes(iso)); setEscala('dia') }

  return (
    <div className="space-y-5">
      <Tarjetas datos={datos} unidades={unidades} proyectos={proyecto ? [] : proyectos} t={t} />

      <div className="flex flex-wrap items-center gap-3">
        <Segmentos valor={escala} onChange={setEscala}
          opciones={[['dia', t('partes.disp.porDia')], ['mes', t('partes.disp.porMes')]]} />
        {escala === 'dia' ? (
          <Navegador etiqueta={fmtMes(mesVisto, true)}
            onPrev={() => setMes(sumarMeses(mesVisto, -1))}
            onNext={() => setMes(sumarMeses(mesVisto, 1))}
            nextDisabled={mesVisto >= inicioMes(hoy)}
            onHoy={mesVisto !== inicioMes(hoy) ? () => setMes(null) : null} t={t} />
        ) : (
          <Navegador etiqueta={String(anioVisto)}
            onPrev={() => setAnio(anioVisto - 1)} onNext={() => setAnio(anioVisto + 1)}
            nextDisabled={anioVisto >= +hoy.slice(0, 4)}
            onHoy={anioVisto !== +hoy.slice(0, 4) ? () => setAnio(null) : null} t={t} />
        )}
        <select value={proyecto} onChange={e => setProyecto(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('partes.disp.todaLaFlota')}</option>
          {proyectos.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('partes.disp.buscar')}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-40" />
        <select value={orden} onChange={e => setOrden(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="codigo">{t('partes.disp.ordenCodigo')}</option>
          <option value="peor">{t('partes.disp.ordenPeor')}</option>
        </select>
      </div>

      {escala === 'dia'
        ? <VistaDia datos={datos} unidades={unidades} mes={mesVisto} orden={orden} onOpenParte={onOpenParte} t={t} />
        : <VistaMes datos={datos} unidades={unidades} anio={anioVisto} orden={orden} proyectos={proyecto ? [proyecto] : proyectos}
            onMes={irAMes} t={t} />}

      {datos.sin_ingreso?.length > 0 && (
        <p className="text-xs text-amber-700">
          {t('partes.disp.sinIngreso', { n: datos.sin_ingreso.length, lista: datos.sin_ingreso.join(', ') })}
        </p>
      )}
      <p className="text-xs text-gray-400">
        {t('partes.disp.nota', { fecha: datos.primer_parte ? fmtMes(datos.primer_parte, true) : '—' })}
      </p>
    </div>
  )
}

// --- KPI cards ---

function Tarjetas({ datos, unidades, proyectos, t }) {
  const p = periodos(datos.hoy)
  const kpi = (desde, hasta) => ({
    total: disponibilidad(datos, unidades, desde, hasta),
    por: proyectos.map(pr => ({ pr, v: disponibilidad(datos, unidades.filter(u => u.proyecto === pr), desde, hasta) })),
  })
  const hoyK = kpi(datos.hoy, datos.hoy)
  const tarjetas = [
    ['mes', kpi(p.mes.desde, p.mes.hasta)],
    ['tresMeses', kpi(p.tresMeses.desde, p.tresMeses.hasta)],
    ['anio', kpi(p.anio.desde, p.anio.hasta)],
    ['doceMeses', kpi(p.doceMeses.desde, p.doceMeses.hasta)],
  ]
  const ind = indicadoresPartes(datos, unidades, p.tresMeses.desde, p.tresMeses.hasta)
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-medium text-gray-500">{t('partes.disp.kpi.hoy')}</p>
        <p className={`text-2xl font-bold mt-1 ${BANDA_TEXTO[banda(hoyK.total.pct)]}`}>{formatoPct(hoyK.total.pct)}</p>
        <p className="text-xs text-gray-500 mt-1">
          {t('partes.disp.kpi.operativos', { n: hoyK.total.disp, total: hoyK.total.disp + hoyK.total.fs })}
        </p>
      </div>
      {tarjetas.map(([k, v]) => (
        <div key={k} className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500">{t(`partes.disp.kpi.${k}`)}</p>
          <p className={`text-2xl font-bold mt-1 ${BANDA_TEXTO[banda(v.total.pct)]}`}>{formatoPct(v.total.pct)}</p>
          {v.por.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {v.por.map(({ pr, v: x }) => (
                <p key={pr} className="text-xs text-gray-500 flex justify-between gap-2">
                  <span className="truncate">{pr}</span><span className="font-medium text-gray-700">{formatoPct(x.pct)}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-medium text-gray-500">{t('partes.disp.kpi.partes3m')}</p>
        <p className="text-2xl font-bold mt-1 text-gray-900">{ind.abiertos}</p>
        <p className="text-xs text-gray-500 mt-1">
          {t('partes.disp.kpi.mttr', { dias: ind.mttr == null ? '—' : ind.mttr.toFixed(1).replace('.', ',') })}
        </p>
      </div>
    </div>
  )
}

// --- Day view: one month, bus by day ---

function VistaDia({ datos, unidades, mes, orden, onOpenParte, t }) {
  const navigate = useNavigate()
  const m = useMemo(() => matriz(datos, unidades, mes, finMes(mes)), [datos, unidades, mes])
  const grupos = agrupar(ordenarFilas(m.filas, orden))
  const serie = m.porDia.map(d => ({ dia: +d.dia.slice(8), fecha: d.dia, pct: pct100(d.pct), fs: d.fs }))
  const cols = m.dias.length + 4

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('partes.disp.diaria', { mes: fmtMes(mes, true) })}</h3>
          <span className={`text-sm font-semibold ${BANDA_TEXTO[banda(m.total.pct)]}`}>
            {t('partes.disp.delMes')}: {formatoPct(m.total.pct)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={serie} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
            <YAxis domain={[(min) => Math.max(0, Math.floor(min - 2)), 100]} tick={{ fontSize: 11 }} unit="%" />
            <Tooltip formatter={(v, n, it) => [`${String(v).replace('.', ',')} % · ${t('partes.disp.fsN', { n: it.payload.fs })}`,
              t('partes.disp.titulo')]} labelFormatter={(_, p) => (p?.[0] ? fmtDia(p[0].payload.fecha) : '')} />
            {m.total.pct != null && <ReferenceLine y={pct100(m.total.pct)} stroke="#94a3b8" strokeDasharray="4 4" />}
            <Line type="monotone" dataKey="pct" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-auto max-h-[70vh]">
        <table className="text-xs border-separate border-spacing-0">
          <thead className="sticky top-0 z-20 bg-white">
            <tr>
              <th className="sticky left-0 z-30 bg-white px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 min-w-[92px]">
                {t('partes.disp.unidad')}
              </th>
              {m.dias.map(d => (
                <th key={d} title={fmtDia(d)}
                  className={`px-0 py-1 w-6 min-w-[24px] text-center font-medium border-b border-gray-200 ${
                    d === datos.hoy ? 'text-gray-900 bg-amber-50' : esFinde(d) ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="text-[9px] uppercase">{inicialDia(d)}</div>
                  <div>{+d.slice(8)}</div>
                </th>
              ))}
              <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-l border-gray-200">{t('partes.disp.disp')}</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">F/S</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">%</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => (
              <FilasGrupo key={g.proyecto} g={g} cols={cols} t={t}>
                {g.filas.map(f => (
                  <tr key={f.unidad.id} className="group">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-3 py-0.5 border-b border-gray-100 whitespace-nowrap">
                      <button type="button" onClick={() => navigate(`/operations/unidades?unidad=${f.unidad.id}`)}
                        title={t('partes.panel.openUnit')}
                        className="font-mono font-medium text-gray-800 hover:text-indigo-600 hover:underline">
                        {f.unidad.codigo}
                      </button>
                    </td>
                    {f.dias.map(d => <CeldaDia key={d.dia} d={d} codigo={f.unidad.codigo} onOpenParte={onOpenParte} t={t} />)}
                    <td className="px-2 text-right tabular-nums text-gray-600 border-b border-l border-gray-100">{f.disp}</td>
                    <td className={`px-2 text-right tabular-nums border-b border-gray-100 ${f.fs ? 'text-rose-600 font-semibold' : 'text-gray-300'}`}>{f.fs}</td>
                    <td className={`px-2 text-right tabular-nums font-semibold border-b border-gray-100 ${BANDA_TEXTO[banda(f.pct)]}`}>
                      {formatoPct(f.pct, 0)}
                    </td>
                  </tr>
                ))}
              </FilasGrupo>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-20 bg-white">
            <tr>
              <td className="sticky left-0 z-30 bg-white px-3 py-1.5 font-semibold text-gray-700 border-t border-gray-300">
                {t('partes.disp.flota')}
              </td>
              {m.porDia.map(d => (
                <td key={d.dia} title={d.pct == null ? '' : `${fmtDia(d.dia)} · ${formatoPct(d.pct)} · ${t('partes.disp.fsN', { n: d.fs })}`}
                  className={`text-center text-[9px] font-semibold tabular-nums border-t border-gray-300 ${BANDA_CELDA[banda(d.pct)]}`}>
                  {d.pct == null ? '' : Math.round(d.pct * 100)}
                </td>
              ))}
              <td className="px-2 text-right tabular-nums font-semibold text-gray-700 border-t border-l border-gray-300">{m.total.disp}</td>
              <td className="px-2 text-right tabular-nums font-semibold text-rose-600 border-t border-gray-300">{m.total.fs}</td>
              <td className={`px-2 text-right tabular-nums font-bold border-t border-gray-300 ${BANDA_TEXTO[banda(m.total.pct)]}`}>
                {formatoPct(m.total.pct)}
              </td>
            </tr>
          </tfoot>
        </table>
        {!unidades.length && <p className="text-gray-400 text-sm text-center py-8">{t('partes.disp.sinUnidades')}</p>}
      </div>
      <Leyenda t={t} />
    </>
  )
}

function CeldaDia({ d, codigo, onOpenParte, t }) {
  if (d.estado == null) return <td className="border-b border-gray-100 bg-gray-50/60" />
  if (d.estado === 'D') {
    return <td title={`${codigo} · ${fmtDia(d.dia)} · ${t('partes.disp.disponible')}`}
      className="border-b border-r border-white bg-emerald-200/80 h-5" />
  }
  return (
    <td className="border-b border-r border-white bg-rose-400 h-5 p-0">
      <button type="button" onClick={() => onOpenParte(d.parte.parte_id)}
        title={`${codigo} · ${fmtDia(d.dia)} · F/S\n#${d.parte.numero} ${d.parte.nombre}`}
        className="block w-full h-5 hover:bg-rose-600 transition-colors" />
    </td>
  )
}

// A project's header row (collapsible) then its units.
function FilasGrupo({ g, cols, t, children }) {
  const [abierto, setAbierto] = useState(true)
  return (
    <>
      <tr>
        <td colSpan={cols} className="p-0 border-b border-gray-200 bg-gray-50">
          <button type="button" onClick={() => setAbierto(a => !a)}
            className="sticky left-0 inline-flex items-center gap-3 px-3 py-1.5 text-left hover:text-gray-900">
            <span className="text-gray-400 w-3">{abierto ? '▾' : '▸'}</span>
            <span className="font-semibold text-gray-800">{g.proyecto}</span>
            <span className="text-gray-500">{t('partes.disp.nUnidades', { n: g.filas.length })}</span>
            <span className={`font-semibold ${BANDA_TEXTO[banda(g.pct)]}`}>{formatoPct(g.pct)}</span>
            <span className="text-gray-500">· {t('partes.disp.fsN', { n: g.fs })}</span>
          </button>
        </td>
      </tr>
      {abierto && children}
    </>
  )
}

// --- Month view: one year, bus by month ---

function VistaMes({ datos, unidades, anio, orden, proyectos, onMes, t }) {
  const navigate = useNavigate()
  const meses = useMemo(() => porMes(datos, unidades, anio), [datos, unidades, anio])
  const filas = unidades.map(u => {
    const disp = meses.reduce((s, m) => s + m.porUnidad[u.id].disp, 0)
    const fs = meses.reduce((s, m) => s + m.porUnidad[u.id].fs, 0)
    return { unidad: u, disp, fs, pct: disp + fs ? disp / (disp + fs) : null }
  })
  const grupos = agrupar(ordenarFilas(filas, orden))
  const totalAnio = {
    disp: meses.reduce((s, m) => s + m.total.disp, 0), fs: meses.reduce((s, m) => s + m.total.fs, 0),
  }
  totalAnio.pct = totalAnio.disp + totalAnio.fs ? totalAnio.disp / (totalAnio.disp + totalAnio.fs) : null
  const serie = meses.map(m => {
    const fila = { mes: fmtMes(m.desde), desde: m.desde, total: pct100(m.total.pct) }
    if (proyectos.length > 1) {
      for (const pr of proyectos) {
        const us = unidades.filter(u => u.proyecto === pr)
        const disp = us.reduce((s, u) => s + m.porUnidad[u.id].disp, 0), fs = us.reduce((s, u) => s + m.porUnidad[u.id].fs, 0)
        fila[pr] = disp + fs ? pct100(disp / (disp + fs)) : null
      }
    }
    return fila
  })
  const minimo = Math.min(...serie.flatMap(s => [s.total, ...proyectos.map(p => s[p])]).filter(v => v != null), 100)

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('partes.disp.mensual', { anio })}</h3>
          <span className={`text-sm font-semibold ${BANDA_TEXTO[banda(totalAnio.pct)]}`}>
            {t('partes.disp.delAnio')}: {formatoPct(totalAnio.pct)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={serie} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}
            onClick={e => { const p = e?.activePayload?.[0]?.payload; if (p && p.total != null) onMes(p.desde) }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis domain={[Math.max(0, Math.floor(minimo - 3)), 100]} tick={{ fontSize: 11 }} unit="%" allowDataOverflow />
            <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v, n) => [v == null ? '—' : `${String(v).replace('.', ',')} %`,
              n === 'total' ? t('partes.disp.flota') : n]} />
            {proyectos.length > 1 && <Legend formatter={n => (n === 'total' ? t('partes.disp.flota') : n)} wrapperStyle={{ fontSize: 12 }} />}
            <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} className="cursor-pointer" />
            {proyectos.length > 1 && proyectos.map((pr, i) => (
              <Bar key={pr} dataKey={pr} fill={COLOR_PROYECTO[i % COLOR_PROYECTO.length]} radius={[4, 4, 0, 0]} className="cursor-pointer" />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs text-gray-400 mt-1">{t('partes.disp.clickMes')}</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-auto max-h-[70vh]">
        <table className="text-xs border-separate border-spacing-0 w-full">
          <thead className="sticky top-0 z-20 bg-white">
            <tr>
              <th className="sticky left-0 z-30 bg-white px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200 min-w-[92px]">
                {t('partes.disp.unidad')}
              </th>
              {meses.map(m => (
                <th key={m.mes} className="px-1 py-2 text-center font-medium text-gray-500 border-b border-gray-200 min-w-[58px]">
                  <button type="button" onClick={() => onMes(m.desde)} disabled={m.total.pct == null}
                    className="capitalize hover:text-indigo-600 disabled:hover:text-gray-500 disabled:cursor-default">
                    {fmtMes(m.desde)}
                  </button>
                </th>
              ))}
              <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-l border-gray-200">F/S</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">{anio}</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => (
              <FilasGrupo key={g.proyecto} g={g} cols={15} t={t}>
                {g.filas.map(f => (
                  <tr key={f.unidad.id} className="group">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-3 py-0.5 border-b border-gray-100 whitespace-nowrap">
                      <button type="button" onClick={() => navigate(`/operations/unidades?unidad=${f.unidad.id}`)}
                        title={t('partes.panel.openUnit')}
                        className="font-mono font-medium text-gray-800 hover:text-indigo-600 hover:underline">
                        {f.unidad.codigo}
                      </button>
                    </td>
                    {meses.map(m => {
                      const c = m.porUnidad[f.unidad.id]
                      return (
                        <td key={m.mes} className="p-0.5 border-b border-gray-100">
                          {c.pct == null ? <div className="h-5" /> : (
                            <button type="button" onClick={() => onMes(m.desde)}
                              title={`${f.unidad.codigo} · ${fmtMes(m.desde, true)}\n${t('partes.disp.disp')}: ${c.disp} · F/S: ${c.fs}`}
                              className={`w-full h-5 rounded text-[11px] font-medium tabular-nums hover:ring-2 hover:ring-gray-300 ${BANDA_CELDA[banda(c.pct)]}`}>
                              {c.fs ? formatoPct(c.pct, 0) : '100 %'}
                            </button>
                          )}
                        </td>
                      )
                    })}
                    <td className={`px-2 text-right tabular-nums border-b border-l border-gray-100 ${f.fs ? 'text-rose-600 font-semibold' : 'text-gray-300'}`}>{f.fs}</td>
                    <td className={`px-2 text-right tabular-nums font-semibold border-b border-gray-100 ${BANDA_TEXTO[banda(f.pct)]}`}>
                      {formatoPct(f.pct)}
                    </td>
                  </tr>
                ))}
              </FilasGrupo>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-20 bg-white">
            <tr>
              <td className="sticky left-0 z-30 bg-white px-3 py-1.5 font-semibold text-gray-700 border-t border-gray-300">{t('partes.disp.flota')}</td>
              {meses.map(m => (
                <td key={m.mes} className="p-0.5 border-t border-gray-300">
                  <div className={`h-6 rounded flex items-center justify-center text-[11px] font-bold tabular-nums ${BANDA_CELDA[banda(m.total.pct)]}`}>
                    {m.total.pct == null ? '' : formatoPct(m.total.pct)}
                  </div>
                </td>
              ))}
              <td className="px-2 text-right tabular-nums font-semibold text-rose-600 border-t border-l border-gray-300">{totalAnio.fs}</td>
              <td className={`px-2 text-right tabular-nums font-bold border-t border-gray-300 ${BANDA_TEXTO[banda(totalAnio.pct)]}`}>
                {formatoPct(totalAnio.pct)}
              </td>
            </tr>
          </tfoot>
        </table>
        {!unidades.length && <p className="text-gray-400 text-sm text-center py-8">{t('partes.disp.sinUnidades')}</p>}
      </div>
      <Leyenda t={t} mensual />
    </>
  )
}

// --- Small pieces ---

function Leyenda({ t, mensual = false }) {
  const item = (cls, txt) => (
    <span className="inline-flex items-center gap-1.5"><span className={`inline-block w-3 h-3 rounded-sm ${cls}`} />{txt}</span>
  )
  return (
    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
      {mensual ? (
        <>
          {item('bg-emerald-100', '≥ 95 %')}
          {item('bg-lime-100', '90 – 95 %')}
          {item('bg-amber-100', '80 – 90 %')}
          {item('bg-rose-100', '< 80 %')}
        </>
      ) : (
        <>
          {item('bg-emerald-200', t('partes.disp.disponible'))}
          {item('bg-rose-400', t('partes.disp.fueraServicio'))}
          {item('bg-gray-100', t('partes.disp.sinDato'))}
        </>
      )}
    </div>
  )
}

function Segmentos({ valor, onChange, opciones }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
      {opciones.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            valor === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
          {label}
        </button>
      ))}
    </div>
  )
}

function Navegador({ etiqueta, onPrev, onNext, nextDisabled, onHoy, t }) {
  return (
    <div className="inline-flex items-center gap-1">
      <button type="button" onClick={onPrev} className="px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100" aria-label="prev">‹</button>
      <span className="text-sm font-semibold text-gray-900 min-w-[130px] text-center capitalize">{etiqueta}</span>
      <button type="button" onClick={onNext} disabled={nextDisabled}
        className="px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent" aria-label="next">›</button>
      {onHoy && (
        <button type="button" onClick={onHoy} className="ml-1 px-2 py-1 rounded-md text-xs text-indigo-600 hover:bg-indigo-50">
          {t('partes.disp.actual')}
        </button>
      )}
    </div>
  )
}
